import React, { useEffect, useRef, useState } from "react";

/**
 * AssistantWidget - CDN-based widget component
 * 
 * This component uses the CDN-delivered embed script to create and manage
 * the AgentWidget iframe. It replaces the previous AssistantIframe component.
 * 
 * @param {Object} props
 * @param {string} props.apiRag - Base URL for RAG chat API
 * @param {string} props.apiInfo - Base URL for info API (models)
 * @param {string} props.apiMemory - Base URL for memory API
 * @param {Function} props.getAssistantToken - Function that returns a Promise resolving to auth token
 * @param {Object} props.config - Additional widget configuration
 * @param {boolean} props.isAuthenticated - Whether user is authenticated
 * @param {string} props.cdnUrl - CDN URL for the embed script (optional, defaults to production CDN)
 * @param {string} props.projectId - Current project ID (GUID) to scope queries to this project
 */
export default function AssistantWidget({
  apiRag,
  apiInfo,
  apiMemory,
  getAssistantToken,
  config = {},
  isAuthenticated = false,
  cdnUrl = null, // Auto-detect if not provided
  projectId = null
}) {
  const widgetInstanceRef = useRef(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const scriptLoadedRef = useRef(false);

  // Auto-detect CDN URL for local development
  const getCdnUrl = () => {
    if (cdnUrl) {
      return cdnUrl;
    }
    
    // Auto-detect local development
    const isLocalDev = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
    
    if (isLocalDev) {
      return 'http://localhost:5173/agent-widget.js';
    }
    
    return 'https://cdn.metabolon.com/agent-widget/latest/agent-widget.js';
  };

  // Initialize widget once script is loaded
  const initializeWidget = React.useCallback(() => {
    if (!window.AgentWidget || widgetInstanceRef.current) {
      console.log('[AssistantWidget] INIT: Skipping - AgentWidget not available or already initialized');
      return;
    }

    try {
      console.log('[AssistantWidget] INIT: Initializing widget with', {
        isAuthenticated,
        projectId
      });

      // Determine initial tenant based on current auth state
      const initialTenant = isAuthenticated ? ["kits_ui"] : ["kits_ui"];
      
      // Build initial metadata filters if projectId is available
      const initialMetadataFilters = (isAuthenticated && projectId)
        ? { "project_id": [projectId] }
        : null;

      const instance = window.AgentWidget.init({
        apiRag: apiRag,
        apiInfo: apiInfo,
        apiMemory: apiMemory,
        getToken: getAssistantToken,
        widget: {
          width: config.widget?.width || 360,
          height: config.widget?.height || 560,
          borderRadius: config.widget?.borderRadius || 12,
          shadow: config.widget?.shadow || "0 8px 30px rgba(0,0,0,.15)"
        },
        welcomeMessage: config.welcomeMessage,
        showModelSelector: false,
        // Use Aperture theme when embedded in Aperture
        theme: 'aperture',
        // Set initial tenant based on current auth state
        accessibleTenantNames: initialTenant,
        // Include metadataFilters in initial config if available
        metadataFilters: initialMetadataFilters,
        onMinimizeChange: (minimized) => {
          setIsMinimized(minimized);
        },
        onAuthChange: (authenticated) => {
          // This can be used if you have an auth event system
        }
      });

      widgetInstanceRef.current = instance;
      setIsWidgetReady(true);
      console.log('[AssistantWidget] INIT: Widget initialized, instance:', !!instance, 'has updateScope:', !!(instance?.updateScope));
      
      // Trigger scope update immediately after initialization
      // This ensures scope is set correctly even if widget initialized before projectId was available
      if (instance?.updateScope) {
        const tenantRestriction = isAuthenticated ? ["kits_ui"] : ["kits_ui"];
        const metadataFilters = (isAuthenticated && projectId)
          ? { "project_id": [projectId] }
          : null;
        
        console.log('[AssistantWidget] INIT: Triggering initial scope update with', {
          tenantRestriction,
          metadataFilters
        });
        
        // Use setTimeout to ensure widget is fully ready
        setTimeout(() => {
          instance.updateScope(tenantRestriction, metadataFilters);
        }, 100);
      }
    } catch (error) {
      console.error('[AssistantWidget] INIT: Failed to initialize widget:', error);
    }
  }, [apiRag, apiInfo, apiMemory, getAssistantToken, config, isAuthenticated, projectId]);

  // Load the CDN script
  useEffect(() => {
    // Check if script is already loaded
    if (window.AgentWidget) {
      scriptLoadedRef.current = true;
      initializeWidget();
      return;
    }

    const scriptUrl = getCdnUrl();
    
    // Check if script tag already exists
    const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existingScript) {
      // Script is loading, wait for it
      existingScript.addEventListener('load', () => {
        scriptLoadedRef.current = true;
        initializeWidget();
      });
      existingScript.addEventListener('error', () => {
        handleScriptError(scriptUrl);
      });
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      initializeWidget();
    };
    script.onerror = () => {
      handleScriptError(scriptUrl);
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts before it loads
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [cdnUrl, initializeWidget]);

  // Handle script loading errors
  const handleScriptError = (url) => {
    console.error('[AssistantWidget] Failed to load CDN script:', url);
    
    // If production CDN fails and we're in local dev, try local fallback
    const isLocalDev = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';
    
    if (!isLocalDev && url.includes('cdn.metabolon.com')) {
      console.warn('[AssistantWidget] CDN unavailable. For local development:');
      console.warn('1. Build the widget: cd AgentWidget && npm run build:all');
      console.warn('2. Serve it: cd AgentWidget/dist && npx http-server -p 5173 --cors');
      console.warn('3. Or deploy to CDN using: node scripts/deploy-cdn.js');
    } else if (isLocalDev) {
      console.warn('[AssistantWidget] Local widget server not found.');
      console.warn('To start local development server:');
      console.warn('1. cd AgentWidget');
      console.warn('2. npm run dev');
      console.warn('   (This runs Vite dev server on port 5173)');
    }
  };


  // Handle authentication state changes
  useEffect(() => {
    if (widgetInstanceRef.current && widgetInstanceRef.current.notifyAuthChange) {
      widgetInstanceRef.current.notifyAuthChange(isAuthenticated);
    }
  }, [isAuthenticated]);

  // Handle scope updates (tenant restrictions and project filters)
  // This effect runs when isAuthenticated or projectId changes after widget is initialized
  useEffect(() => {
    if (!isWidgetReady) {
      console.log('[AssistantWidget] SCOPE UPDATE: Widget not ready yet, skipping');
      // Widget not ready yet - initial scope will be set during initialization
      return;
    }

    console.log('[AssistantWidget] SCOPE UPDATE: Effect running with', {
      hasWidgetInstance: !!widgetInstanceRef.current,
      hasUpdateScope: !!(widgetInstanceRef.current?.updateScope),
      isAuthenticated,
      projectId
    });

    if (!widgetInstanceRef.current?.updateScope) {
      console.log('[AssistantWidget] SCOPE UPDATE: updateScope not available, skipping');
      return;
    }

    const tenantRestriction = isAuthenticated ? ["kits_ui"] : ["kits_ui"];
    const metadataFilters = (isAuthenticated && projectId)
      ? { "project_id": [projectId] }
      : null;

    console.log('[AssistantWidget] SCOPE UPDATE: Calling updateScope with', {
      tenantRestriction,
      metadataFilters
    });

    widgetInstanceRef.current.updateScope(tenantRestriction, metadataFilters);
    console.log('[AssistantWidget] SCOPE UPDATE: updateScope called');
  }, [isAuthenticated, projectId, isWidgetReady]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (widgetInstanceRef.current && widgetInstanceRef.current.destroy) {
        widgetInstanceRef.current.destroy();
        widgetInstanceRef.current = null;
      }
    };
  }, []);

  // This component doesn't render anything - the widget is injected by the embed script
  return null;
}

